// One import flow for any flat table: pick a file, we find the table and guess the columns, the user confirms or
// changes the mapping, then the usual preview. Mappings are remembered per import type in this browser.
import { useMemo, useState, type ReactNode } from "react";
import { FileUp } from "lucide-react";
import { pickFile, readWorkbook, type Sheet } from "@/lib/xlsx";
import { applyMapping, colLetter, detectTable, guessMapping, normHeader, str, type FieldSpec, type MappedRow } from "@/lib/mapping";
import { Badge, Button, Modal, Select, cn, errMsg, useToast } from "./ui";
import { PreviewModal, type Preview } from "./Importer";

export interface ImportMode {
  value: string;
  label: string;
  hint?: string;
}

interface State {
  file: string;
  sheets: Sheet[];
  sheet: number;
  headerRow: number;
  mapping: Record<string, number | null>;
}

const memKey = (k: string) => `evm-colmap:${k}`;

export function MappedImportButton({
  label = "Import",
  fields,
  rememberKey,
  modes,
  build,
  disabled,
  intro,
}: {
  label?: string;
  fields: FieldSpec[];
  /** Remembers the user's column choices for this kind of import. */
  rememberKey: string;
  modes?: ImportMode[];
  build: (rows: MappedRow[], ctx: { mode?: string; fileName: string }) => Preview | Promise<Preview>;
  disabled?: boolean;
  intro?: ReactNode;
}) {
  const toast = useToast();
  const [st, setSt] = useState<State | null>(null);
  const [mode, setMode] = useState(modes?.[0]?.value);
  const [preview, setPreview] = useState<Preview | null>(null);
  const remembered = (): Record<string, string> => {
    try {
      return JSON.parse(localStorage.getItem(memKey(rememberKey)) ?? "{}");
    } catch {
      return {};
    }
  };
  const open = async () => {
    const f = await pickFile(".xlsx");
    if (!f) return;
    try {
      const sheets = await readWorkbook(await f.arrayBuffer());
      const t = detectTable(sheets, fields);
      setSt({ file: f.name, sheets, sheet: t.sheet, headerRow: t.headerRow, mapping: guessMapping(sheets[t.sheet].rows[t.headerRow] ?? [], fields, remembered()) });
    } catch (e) {
      toast({ text: `Couldn't read ${f.name}: ${errMsg(e)}`, tone: "error" });
    }
  };
  const rows = st?.sheets[st.sheet]?.rows ?? [];
  const header = rows[st?.headerRow ?? 0] ?? [];
  const width = Math.max(header.length, ...rows.slice((st?.headerRow ?? 0) + 1, (st?.headerRow ?? 0) + 30).map((r) => r?.length ?? 0));
  const mapped = useMemo(() => (st ? applyMapping(rows, st.headerRow, st.mapping) : []), [st, rows]);
  const missing = fields.filter((f) => f.required && (st?.mapping[f.key] ?? null) === null);
  const dupCols = st ? Object.values(st.mapping).filter((c, i, a) => c !== null && a.indexOf(c) !== i) : [];
  const setField = (key: string, col: number | null) => st && setSt({ ...st, mapping: { ...st.mapping, [key]: col } });
  const reguess = (sheet: number, headerRow: number) =>
    st && setSt({ ...st, sheet, headerRow, mapping: guessMapping(st.sheets[sheet].rows[headerRow] ?? [], fields, remembered()) });
  const next = async () => {
    if (!st) return;
    const mem = remembered();
    for (const [k, c] of Object.entries(st.mapping)) if (c !== null && str(header[c])) mem[k] = normHeader(header[c]);
    localStorage.setItem(memKey(rememberKey), JSON.stringify(mem));
    try {
      setPreview(await build(mapped, { mode, fileName: st.file }));
    } catch (e) {
      toast({ text: errMsg(e), tone: "error" });
    }
  };
  const colOption = (c: number) => `${colLetter(c)}: ${str(header[c]).replace(/\s+/g, " ") || "(no header)"}`;
  return (
    <>
      <Button onClick={open} disabled={disabled}>
        <FileUp className="size-4" /> {label}
      </Button>
      {st && !preview && (
        <Modal
          open
          onClose={() => setSt(null)}
          title="Match your columns"
          wide="xl"
          footer={
            <>
              {missing.length > 0 && <span className="mr-auto self-center text-xs text-red-600">Pick a column for: {missing.map((f) => f.label).join(", ")}</span>}
              <Button onClick={() => setSt(null)}>Cancel</Button>
              <Button variant="primary" disabled={missing.length > 0 || dupCols.length > 0 || !mapped.length} onClick={next}>
                Next: preview {mapped.length} rows
              </Button>
            </>
          }
        >
          <div className="space-y-4">
            {intro}
            <div className="flex flex-wrap items-end gap-3 text-sm">
              <label className="space-y-1">
                <span className="block text-xs font-medium text-slate-600">Sheet</span>
                <Select className="w-56" value={st.sheet} onChange={(e) => {
                  const s = Number(e.target.value);
                  reguess(s, detectTable([st.sheets[s]], fields).headerRow);
                }}>
                  {st.sheets.map((s, i) => (
                    <option key={i} value={i}>
                      {s.name}
                    </option>
                  ))}
                </Select>
              </label>
              <label className="space-y-1">
                <span className="block text-xs font-medium text-slate-600">Header row</span>
                <Select className="w-96" value={st.headerRow} onChange={(e) => reguess(st.sheet, Number(e.target.value))}>
                  {rows.slice(0, 30).map((r, i) => (
                    <option key={i} value={i}>
                      Row {i + 1}: {(r ?? []).map(str).filter(Boolean).join(" | ").replace(/\s+/g, " ").slice(0, 70) || "(empty)"}
                    </option>
                  ))}
                </Select>
              </label>
              {modes && (
                <label className="space-y-1">
                  <span className="block text-xs font-medium text-slate-600">How to import</span>
                  <Select className="w-72" value={mode} onChange={(e) => setMode(e.target.value)}>
                    {modes.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </Select>
                </label>
              )}
              <span className="text-xs text-slate-500">
                {st.file} · {mapped.length} data rows
              </span>
            </div>
            {modes && <p className="-mt-2 text-xs text-slate-500">{modes.find((m) => m.value === mode)?.hint}</p>}
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-slate-500">
                <tr>
                  <th className="w-48 py-1">Our field</th>
                  <th className="w-80 py-1">Your column</th>
                  <th className="py-1">First values</th>
                </tr>
              </thead>
              <tbody>
                {fields.map((f) => {
                  const c = st.mapping[f.key];
                  const dup = c !== null && dupCols.includes(c);
                  return (
                    <tr key={f.key} className="border-t border-slate-100">
                      <td className="py-1.5 pr-2">
                        {f.label}
                        {f.required && <span className="text-red-600"> *</span>}
                        {f.hint && <div className="text-[11px] text-slate-500">{f.hint}</div>}
                      </td>
                      <td className="py-1.5 pr-2">
                        <Select aria-label={`Column for ${f.label}`} className={cn(dup && "border-red-400", c === null && f.required && "border-red-400")} value={c ?? ""} onChange={(e) => setField(f.key, e.target.value === "" ? null : Number(e.target.value))}>
                          <option value="">(don't import)</option>
                          {Array.from({ length: width }, (_, i) => (
                            <option key={i} value={i}>
                              {colOption(i)}
                            </option>
                          ))}
                        </Select>
                        {dup && <div className="text-[11px] text-red-600">This column is already used by another field</div>}
                      </td>
                      <td className="py-1.5 text-xs text-slate-600">
                        {c === null ? (
                          <span className="text-slate-400">-</span>
                        ) : (
                          mapped
                            .slice(0, 4)
                            .map((r) => str(r.values[f.key]) || "·")
                            .map((v, i) => (
                              <Badge key={i} className="mr-1">
                                {v.slice(0, 30)}
                              </Badge>
                            ))
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {(() => {
              const unused = Array.from({ length: width }, (_, i) => i).filter((i) => !Object.values(st.mapping).includes(i) && str(header[i]));
              return unused.length > 0 && <p className="text-xs text-slate-500">Not imported: {unused.map(colOption).join(", ")}</p>;
            })()}
          </div>
        </Modal>
      )}
      {preview && (
        <PreviewModal
          preview={preview}
          onBack={() => setPreview(null)}
          onClose={() => {
            setPreview(null);
            setSt(null);
          }}
        />
      )}
    </>
  );
}
